import { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ScatterChart, Scatter, ReferenceLine } from "recharts";
import { Sparkles, TrendingUp } from "lucide-react";

export default function TuningTab({ id }) {
  const [tuning, setTuning] = useState(null);
  useEffect(() => {
    const load = () => endpoints.tuning(id).then((r) => setTuning(r.data.tuning));
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [id]);

  if (!tuning) return <div className="text-sm text-muted-foreground">Hyperparameter tuning runs after model selection…</div>;

  if (tuning.skipped) {
    return (
      <div className="border border-border rounded-sm p-6 bg-card">
        <div className="label-mono mb-2 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-accent" /> // TUNING SKIPPED
        </div>
        <p className="text-sm text-muted-foreground">{tuning.reason}</p>
      </div>
    );
  }
  if (tuning.error) {
    return (
      <div className="border border-destructive/30 rounded-sm p-6 bg-destructive/5">
        <div className="label-mono text-destructive mb-2">// TUNING ERROR</div>
        <p className="text-sm text-destructive">{tuning.error}</p>
      </div>
    );
  }

  const improved = tuning.improvement > 0;

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card label="MODEL" value={tuning.model} />
        <Card label="BASELINE CV" value={tuning.baseline_score?.toFixed(4)} />
        <Card label="BEST CV" value={tuning.best_score?.toFixed(4)} accent />
        <Card label="IMPROVEMENT" value={`${improved ? "+" : ""}${tuning.improvement?.toFixed(4)}`} highlight={improved} />
        <Card label="TRIALS" value={tuning.n_trials} />
      </div>

      <div>
        <div className="label-mono mb-3 flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5 text-accent" /> // TRIAL EVOLUTION
        </div>
        <div className="border border-border rounded-sm p-4 bg-card h-72">
          <ResponsiveContainer>
            <LineChart data={tuning.running_best} margin={{ left: 0, right: 20 }}>
              <XAxis dataKey="number" stroke="hsl(0 0% 45%)" fontSize={10} label={{ value: "trial", fill: "hsl(0 0% 45%)", fontSize: 10, position: "insideBottom", offset: -3 }} />
              <YAxis stroke="hsl(0 0% 45%)" fontSize={10} />
              <Tooltip contentStyle={{ background: "hsl(0 0% 6%)", border: "1px solid hsl(0 0% 14%)" }} />
              <ReferenceLine y={tuning.baseline_score} stroke="hsl(0 0% 40%)" strokeDasharray="3 3" label={{ value: "baseline", fill: "hsl(0 0% 50%)", fontSize: 10, position: "insideTopRight" }} />
              <Line type="stepAfter" dataKey="running_best" stroke="hsl(45 93% 47%)" strokeWidth={2} dot={false} name="best so far" />
              <Line type="monotone" dataKey="score" stroke="hsl(217 91% 60%)" strokeWidth={1} dot={{ r: 2 }} name="trial score" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {tuning.best_params && Object.keys(tuning.best_params).length > 0 && (
        <div>
          <div className="label-mono mb-3">// BEST HYPERPARAMETERS</div>
          <div className="border border-border rounded-sm p-5 bg-card">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
              {Object.entries(tuning.best_params).map(([k, v]) => (
                <div key={k} className="flex justify-between text-xs border-b border-border pb-1.5" data-testid={`param-${k}`}>
                  <span className="font-mono text-muted-foreground">{k}</span>
                  <span className="num font-medium text-accent">{typeof v === "number" ? v.toFixed(4) : String(v)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tuning.trials?.length > 0 && (
        <div>
          <div className="label-mono mb-3">// TRIAL SCATTER</div>
          <div className="border border-border rounded-sm p-4 bg-card h-64">
            <ResponsiveContainer>
              <ScatterChart>
                <XAxis dataKey="number" stroke="hsl(0 0% 45%)" fontSize={10} name="trial" />
                <YAxis dataKey="score" stroke="hsl(0 0% 45%)" fontSize={10} name="score" />
                <Tooltip contentStyle={{ background: "hsl(0 0% 6%)", border: "1px solid hsl(0 0% 14%)" }} />
                <ReferenceLine y={tuning.baseline_score} stroke="hsl(0 0% 40%)" strokeDasharray="3 3" />
                <Scatter data={tuning.trials.filter((t) => t.score !== null)} fill="hsl(45 93% 47%)" />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ label, value, accent, highlight }) {
  return (
    <div className={`border rounded-sm p-4 bg-card ${highlight ? "border-emerald-500/40 glow-amber" : "border-border"}`}>
      <div className="label-mono">{label}</div>
      <div className={`num font-display font-bold text-lg mt-1 truncate ${accent ? "text-accent" : highlight ? "text-emerald-400" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}
